/**
 * Portfolio page — Phase 2.
 *
 * Tabs:
 *   - One tab per D1 portfolio (ISA, GIA, Crypto)
 *   - Pensions (read-only from Forkcast KV)
 *   - Nora JISA (read-only from Forkcast KV)
 *
 * Each D1 portfolio tab shows:
 *   - Holdings table (units, avg cost, current price, value, P&L)
 *   - "Update prices" button (Yahoo Finance via worker)
 *   - CSV import section (drag-drop, client-side preview, confirm)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  importApi,
  portfolioApi,
  type HoldingWithPrice,
  type HoldingsResponse,
  type ImportResult,
  type NoraFund,
  type NoraResponse,
  type PensionPot,
  type PensionsResponse,
  type Portfolio,
  type PriceRefreshResult,
} from '../api.ts';
import { deltaClass, formatDate, formatDelta, formatGbp, formatPercent } from '../lib/format.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

type TabId = string; // portfolio id, 'pensions', or 'nora'

type PortfolioTabState = {
  data: HoldingsResponse | null;
  loading: boolean;
  error: string;
  refreshing: boolean;
  refreshResult: PriceRefreshResult | null;
  // CSV import
  importFile: File | null;
  importPreview: { headers: string[]; rows: string[][] } | null;
  importing: boolean;
  importResult: ImportResult | null;
  importError: string;
  // Drag state
  dragging: boolean;
};

// ─── Client-side CSV preview parser ──────────────────────────────────────────

function parseCsvPreview(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split('\n').filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };

  function parseLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { current += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === ',' && !inQuote) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  }

  return {
    headers: parseLine(lines[0]),
    rows: lines.slice(1, 6).map(parseLine),
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pensionTypeLabel(type: PensionPot['type']): string {
  switch (type) {
    case 'employer-dc': return 'Employer DC';
    case 'personal-dc': return 'Personal DC';
    case 'db':          return 'Defined Benefit';
    case 'state':       return 'State Pension';
    default:            return type;
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Portfolio() {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>('');
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState('');

  // Per-tab state (keyed by tab id)
  const [tabState, setTabState] = useState<Record<TabId, PortfolioTabState>>({});

  // Pensions / Nora (separate from the generic tab state)
  const [pensions, setPensions] = useState<PensionsResponse | null>(null);
  const [pensionsLoading, setPensionsLoading] = useState(false);
  const [pensionsError, setPensionsError] = useState('');

  const [nora, setNora] = useState<NoraResponse | null>(null);
  const [noraLoading, setNoraLoading] = useState(false);
  const [noraError, setNoraError] = useState('');

  // Load portfolio list on mount
  useEffect(() => {
    portfolioApi
      .list()
      .then((list) => {
        setPortfolios(list);
        if (list.length > 0) setActiveTab(list[0].id);
      })
      .catch((e: Error) => setPortfolioError(e.message))
      .finally(() => setPortfolioLoading(false));
  }, []);

  // Load data when tab changes
  useEffect(() => {
    if (!activeTab) return;

    if (activeTab === 'pensions') {
      if (pensions || pensionsLoading) return;
      setPensionsLoading(true);
      portfolioApi.pensions()
        .then(setPensions)
        .catch((e: Error) => setPensionsError(e.message))
        .finally(() => setPensionsLoading(false));
      return;
    }

    if (activeTab === 'nora') {
      if (nora || noraLoading) return;
      setNoraLoading(true);
      portfolioApi.nora()
        .then(setNora)
        .catch((e: Error) => setNoraError(e.message))
        .finally(() => setNoraLoading(false));
      return;
    }

    // D1 portfolio tab
    if (tabState[activeTab]?.data || tabState[activeTab]?.loading) return;
    setTabState((prev) => ({
      ...prev,
      [activeTab]: prev[activeTab] ?? initialTabState(),
    }));
    loadTabData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  function initialTabState(): PortfolioTabState {
    return {
      data: null,
      loading: true,
      error: '',
      refreshing: false,
      refreshResult: null,
      importFile: null,
      importPreview: null,
      importing: false,
      importResult: null,
      importError: '',
      dragging: false,
    };
  }

  function patchTab(id: TabId, patch: Partial<PortfolioTabState>) {
    setTabState((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? initialTabState()), ...patch },
    }));
  }

  async function loadTabData(id: TabId) {
    patchTab(id, { loading: true, error: '' });
    try {
      const data = await portfolioApi.holdings(id);
      patchTab(id, { data, loading: false });
    } catch (e: unknown) {
      patchTab(id, { loading: false, error: e instanceof Error ? e.message : 'Failed to load' });
    }
  }

  async function handleRefreshPrices(id: TabId) {
    patchTab(id, { refreshing: true, refreshResult: null });
    try {
      const result = await portfolioApi.refreshPrices(id);
      patchTab(id, { refreshing: false, refreshResult: result });
      // Re-fetch holdings to pick up new prices
      const fresh = await portfolioApi.holdings(id);
      patchTab(id, { data: fresh });
    } catch (e: unknown) {
      patchTab(id, {
        refreshing: false,
        refreshResult: null,
        error: e instanceof Error ? e.message : 'Price refresh failed',
      });
    }
  }

  async function handleImport(id: TabId) {
    const state = tabState[id];
    if (!state?.importFile) return;
    patchTab(id, { importing: true, importError: '', importResult: null });
    try {
      const result = await importApi.upload(state.importFile, id);
      patchTab(id, { importing: false, importResult: result, importFile: null, importPreview: null });
      // Re-fetch holdings to show newly imported transactions
      const fresh = await portfolioApi.holdings(id);
      patchTab(id, { data: fresh });
    } catch (e: unknown) {
      patchTab(id, {
        importing: false,
        importError: e instanceof Error ? e.message : 'Import failed',
      });
    }
  }

  function handleFileSelected(id: TabId, file: File) {
    patchTab(id, { importFile: file, importResult: null, importError: '' });
    file.text().then((text) => {
      patchTab(id, { importPreview: parseCsvPreview(text) });
    });
  }

  function handleCancelImport(id: TabId) {
    patchTab(id, { importFile: null, importPreview: null, importResult: null, importError: '' });
  }

  // ─── Tab list ───────────────────────────────────────────────────────────────

  const tabs: { id: TabId; label: string }[] = [
    ...portfolios.map((p) => ({ id: p.id, label: p.name })),
    { id: 'pensions', label: 'Pensions' },
    { id: 'nora', label: 'Nora JISA' },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (portfolioLoading) return <Spinner />;
  if (portfolioError) return <ErrorBanner message={portfolioError} />;

  const currentState = tabState[activeTab];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Investment Portfolio</h1>

      {/* Tab bar */}
      <div className="border-b border-gray-800 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={[
                'px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap',
                activeTab === t.id
                  ? 'bg-gray-900 border border-b-0 border-gray-700 text-white'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50',
              ].join(' ')}
            >
              {t.id === 'pensions' || t.id === 'nora' ? (
                <span className="flex items-center gap-1.5">
                  {t.label}
                  <span className="text-xs text-gray-500 font-normal">KV</span>
                </span>
              ) : (
                t.label
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'pensions' ? (
        <PensionsTab data={pensions} loading={pensionsLoading} error={pensionsError} />
      ) : activeTab === 'nora' ? (
        <NoraTab data={nora} loading={noraLoading} error={noraError} />
      ) : currentState ? (
        <D1PortfolioTab
          portfolioId={activeTab}
          state={currentState}
          onRefreshPrices={() => handleRefreshPrices(activeTab)}
          onFileSelected={(f) => handleFileSelected(activeTab, f)}
          onCancelImport={() => handleCancelImport(activeTab)}
          onImport={() => handleImport(activeTab)}
          onDragChange={(dragging) => patchTab(activeTab, { dragging })}
        />
      ) : (
        <Spinner />
      )}
    </div>
  );
}

// ─── D1 Portfolio Tab ─────────────────────────────────────────────────────────

function D1PortfolioTab({
  portfolioId: _portfolioId,
  state,
  onRefreshPrices,
  onFileSelected,
  onCancelImport,
  onImport,
  onDragChange,
}: {
  portfolioId?: string;
  state: PortfolioTabState;
  onRefreshPrices: () => void;
  onFileSelected: (f: File) => void;
  onCancelImport: () => void;
  onImport: () => void;
  onDragChange: (d: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      onDragChange(false);
      const file = e.dataTransfer.files[0];
      if (file) onFileSelected(file);
    },
    [onDragChange, onFileSelected],
  );

  if (state.loading) return <Spinner />;
  if (state.error && !state.data) return <ErrorBanner message={state.error} />;
  if (!state.data) return <Spinner />;

  const { portfolio, holdings } = state.data;
  const hasTickerHoldings = holdings.some((h) => h.ticker);
  const pricedHoldings = holdings.filter((h) => h.price_gbp !== null);
  const totalCost = holdings.reduce((s, h) => s + h.cost_basis_gbp, 0);
  const totalValue = pricedHoldings.reduce((s, h) => s + h.units * (h.price_gbp ?? 0), 0);
  const totalPnL = totalValue - pricedHoldings.reduce((s, h) => s + h.cost_basis_gbp, 0);

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total Cost" value={formatGbp(totalCost)} />
        <SummaryCard
          label={`Value (${pricedHoldings.length}/${holdings.length} priced)`}
          value={pricedHoldings.length > 0 ? formatGbp(totalValue) : '--'}
        />
        <SummaryCard
          label="Unrealised P&L"
          value={pricedHoldings.length > 0 ? formatDelta(totalPnL) : '--'}
          coloured={pricedHoldings.length > 0}
          delta={totalPnL}
        />
        <SummaryCard
          label="Return"
          value={
            pricedHoldings.length > 0 && totalCost > 0
              ? formatPercent((totalPnL / totalCost) * 100)
              : '--'
          }
          coloured={pricedHoldings.length > 0}
          delta={totalPnL}
        />
      </div>

      {/* Holdings table */}
      <section className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
            Holdings {holdings.length > 0 && `(${holdings.length})`}
          </h2>
          <div className="flex items-center gap-3">
            {state.refreshResult && (
              <span className="text-xs text-gray-500">
                {state.refreshResult.refreshed > 0
                  ? `Updated ${state.refreshResult.refreshed} price${state.refreshResult.refreshed !== 1 ? 's' : ''}`
                  : state.refreshResult.message ?? 'No prices updated'}
              </span>
            )}
            {hasTickerHoldings && (
              <button
                onClick={onRefreshPrices}
                disabled={state.refreshing}
                className="text-xs bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-300 px-3 py-1.5 rounded-lg border border-gray-700 transition-colors"
              >
                {state.refreshing ? 'Fetching prices…' : 'Update prices'}
              </button>
            )}
          </div>
        </div>

        {holdings.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 text-sm">
            No holdings yet — import a CSV below to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Units</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Cost</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">P&amp;L</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Divs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/60">
                {holdings.map((h, i) => (
                  <HoldingRow key={`${h.ticker ?? h.asset_name}-${i}`} holding={h} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!hasTickerHoldings && holdings.length > 0 && (
          <p className="px-6 py-3 text-xs text-gray-600 border-t border-gray-800">
            This portfolio's CSV format doesn't include ticker symbols, so live prices can't be fetched automatically.
          </p>
        )}
      </section>

      {/* Import section */}
      <section className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Import CSV</h2>

        {!state.importFile ? (
          <div
            className={[
              'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
              state.dragging
                ? 'border-brand-500 bg-brand-900/20'
                : 'border-gray-700 hover:border-gray-600',
            ].join(' ')}
            onDragOver={(e) => { e.preventDefault(); onDragChange(true); }}
            onDragLeave={() => onDragChange(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          >
            <p className="text-gray-400 mb-1">Drop a CSV here, or click to select</p>
            <p className="text-xs text-gray-600">
              Supported: {portfolio.provider === 'freetrade' ? 'Freetrade' : portfolio.provider === 'trading212' ? 'Trading 212' : 'Crypto canonical'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFileSelected(f); }}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* File name */}
            <div className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-2">
              <span className="text-sm text-gray-300">{state.importFile.name}</span>
              <button onClick={onCancelImport} className="text-xs text-gray-500 hover:text-gray-300">
                Remove
              </button>
            </div>

            {/* Preview */}
            {state.importPreview && state.importPreview.headers.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="text-xs w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      {state.importPreview.headers.slice(0, 8).map((h, i) => (
                        <th key={i} className="px-3 py-2 text-left text-gray-400 font-medium whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                      {state.importPreview.headers.length > 8 && (
                        <th className="px-3 py-2 text-gray-600">+{state.importPreview.headers.length - 8} more</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {state.importPreview.rows.map((row, ri) => (
                      <tr key={ri} className="hover:bg-gray-800/40">
                        {row.slice(0, 8).map((cell, ci) => (
                          <td key={ci} className="px-3 py-1.5 text-gray-300 whitespace-nowrap max-w-32 truncate">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="px-3 py-1.5 text-xs text-gray-600 border-t border-gray-700">
                  Showing first {state.importPreview.rows.length} rows
                </p>
              </div>
            )}

            {/* Import errors */}
            {state.importError && (
              <p className="text-sm text-red-400 bg-red-950/30 border border-red-900/40 rounded-lg px-4 py-2">
                {state.importError}
              </p>
            )}

            {/* Confirm button */}
            {!state.importResult && (
              <div className="flex justify-end">
                <button
                  onClick={onImport}
                  disabled={state.importing}
                  className="bg-brand-700 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
                >
                  {state.importing ? 'Importing…' : 'Confirm import'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Import result */}
        {state.importResult && (
          <ImportResultBanner result={state.importResult} />
        )}
      </section>
    </div>
  );
}

// ─── Pensions Tab ─────────────────────────────────────────────────────────────

function PensionsTab({
  data,
  loading,
  error,
}: {
  data: { pots: PensionPot[] } | null;
  loading: boolean;
  error: string;
}) {
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data || data.pots.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
        No pension data found in Forkcast KV.
      </div>
    );
  }

  const byOwner = data.pots.reduce<Record<string, PensionPot[]>>((acc, pot) => {
    const key = pot.owner;
    acc[key] = [...(acc[key] ?? []), pot];
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <p className="text-xs text-gray-600">
        Read-only from Forkcast KV — edit pension data in Forkcast.
      </p>
      {Object.entries(byOwner).map(([owner, pots]) => {
        const total = pots.reduce((s, p) => s + (p.currentValue ?? 0), 0);
        return (
          <section key={owner} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-white">{owner}</h2>
              <span className="text-sm font-mono text-gray-300">{formatGbp(total)}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {pots.map((pot) => (
                <div key={pot.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-white leading-snug">{pot.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{pot.provider} · {pensionTypeLabel(pot.type)}</p>
                    </div>
                    <p className="text-base font-bold font-mono text-white shrink-0">
                      {formatGbp(pot.currentValue ?? 0)}
                    </p>
                  </div>
                  {pot.valueUpdatedAt && (
                    <p className="text-xs text-gray-600">
                      Updated {formatDate(pot.valueUpdatedAt)}
                    </p>
                  )}
                  {pot.type === 'db' && pot.accruedAnnualIncome != null && (
                    <div className="text-xs text-gray-500 space-y-0.5 pt-1 border-t border-gray-800 mt-2">
                      <p>Accrued: {formatGbp(pot.accruedAnnualIncome)}/yr</p>
                      {pot.projectedAnnualIncome != null && (
                        <p>Projected: {formatGbp(pot.projectedAnnualIncome)}/yr</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─── Nora JISA Tab ────────────────────────────────────────────────────────────

function NoraTab({
  data,
  loading,
  error,
}: {
  data: NoraResponse | null;
  loading: boolean;
  error: string;
}) {
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data || !data.nora) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
        No Nora JISA data found in Forkcast KV.
      </div>
    );
  }

  const { nora, fund_price_gbp, total_value_gbp } = data;
  const totalUnits = nora.funds.reduce((s, f) => s + (f.units ?? 0), 0);

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-600">
        Read-only from Forkcast KV — edit in Forkcast.
      </p>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Value</p>
          <p className="text-2xl font-bold text-white">
            {total_value_gbp != null ? formatGbp(total_value_gbp) : '--'}
          </p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Units</p>
          <p className="text-2xl font-bold text-white font-mono">
            {totalUnits.toLocaleString('en-GB', { maximumFractionDigits: 4 })}
          </p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Fund Price (VAFTGAG)</p>
          <p className="text-2xl font-bold text-white font-mono">
            {fund_price_gbp != null ? formatGbp(fund_price_gbp, true) : '--'}
          </p>
          <p className="text-xs text-gray-600 mt-1">Refreshed daily by Forkcast</p>
        </div>
      </div>

      {nora.funds.length > 0 && (
        <section className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Funds</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">Fund</th>
                <th className="text-right px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">Units</th>
                <th className="text-right px-6 py-3 text-xs text-gray-500 uppercase tracking-wider">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/60">
              {nora.funds.map((f: NoraFund) => {
                const value = fund_price_gbp != null ? f.units * fund_price_gbp : null;
                return (
                  <tr key={f.isin} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-6 py-3">
                      <p className="text-gray-200">{f.name}</p>
                      <p className="text-xs text-gray-600">{f.isin}</p>
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-gray-300">
                      {f.units.toLocaleString('en-GB', { maximumFractionDigits: 4 })}
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-white">
                      {value != null ? formatGbp(value) : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <p className="text-xs text-gray-600">
        JISA opened {nora.openedDate ? formatDate(nora.openedDate) : 'unknown'} ·
        Vanguard FTSE Global All Cap (VAFTGAG)
      </p>
    </div>
  );
}

// ─── Holdings row ─────────────────────────────────────────────────────────────

function HoldingRow({ holding: h }: { holding: HoldingWithPrice }) {
  const avgCost = h.units > 0 ? h.cost_basis_gbp / h.units : null;
  const currentValue = h.price_gbp != null ? h.units * h.price_gbp : null;
  const pnl = currentValue != null ? currentValue - h.cost_basis_gbp : null;
  const pnlPct = pnl != null && h.cost_basis_gbp > 0 ? (pnl / h.cost_basis_gbp) * 100 : null;

  return (
    <tr className="hover:bg-gray-800/30 transition-colors">
      <td className="px-6 py-3">
        <p className="text-gray-200 leading-snug">{h.asset_name}</p>
        <p className="text-xs text-gray-600">
          {[h.ticker, h.isin].filter(Boolean).join(' · ') || 'No ticker'}
        </p>
      </td>
      <td className="px-4 py-3 text-right font-mono text-gray-300 tabular-nums">
        {h.units.toLocaleString('en-GB', { maximumFractionDigits: 4 })}
      </td>
      <td className="px-4 py-3 text-right font-mono text-gray-300 tabular-nums">
        {avgCost != null ? formatGbp(avgCost, true) : '--'}
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">
        {h.price_gbp != null ? (
          <span className="text-white">{formatGbp(h.price_gbp, true)}</span>
        ) : (
          <span className="text-gray-600">--</span>
        )}
      </td>
      <td className="px-4 py-3 text-right font-mono text-white tabular-nums">
        {currentValue != null ? formatGbp(currentValue) : '--'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {pnl != null ? (
          <div>
            <p className={`font-mono text-sm font-medium ${deltaClass(pnl)}`}>
              {formatDelta(pnl)}
            </p>
            {pnlPct != null && (
              <p className={`text-xs ${deltaClass(pnlPct)}`}>
                {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
              </p>
            )}
          </div>
        ) : (
          <span className="text-gray-600">--</span>
        )}
      </td>
      <td className="px-6 py-3 text-right font-mono text-gray-500 tabular-nums text-sm">
        {h.dividends_received > 0 ? formatGbp(h.dividends_received) : '--'}
      </td>
    </tr>
  );
}

// ─── Import result banner ─────────────────────────────────────────────────────

function ImportResultBanner({ result }: { result: ImportResult }) {
  return (
    <div className="bg-emerald-950/40 border border-emerald-900/50 rounded-xl p-4 space-y-2">
      <p className="text-sm font-medium text-emerald-400">
        Import complete — {result.imported} transaction{result.imported !== 1 ? 's' : ''} added
        {result.skipped_duplicates > 0 && `, ${result.skipped_duplicates} duplicate${result.skipped_duplicates !== 1 ? 's' : ''} skipped`}
      </p>
      {result.date_range && (
        <p className="text-xs text-emerald-600">
          Date range: {formatDate(result.date_range.from)} – {formatDate(result.date_range.to)}
        </p>
      )}
      {result.errors.length > 0 && (
        <div className="text-xs text-amber-400 space-y-0.5">
          <p className="font-medium">Warnings ({result.errors.length}):</p>
          {result.errors.slice(0, 5).map((e, i) => <p key={i}>{e}</p>)}
          {result.errors.length > 5 && <p>…and {result.errors.length - 5} more</p>}
        </div>
      )}
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  coloured,
  delta,
}: {
  label: string;
  value: string;
  coloured?: boolean;
  delta?: number;
}) {
  const colour = coloured && delta != null ? deltaClass(delta) : 'text-white';
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-bold font-mono tabular-nums ${colour}`}>{value}</p>
    </div>
  );
}

// ─── Shared ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="bg-red-950/40 border border-red-900/50 rounded-xl p-6 text-red-400 text-sm">
      {message}
    </div>
  );
}
